/**
 * Desenho de texto e legendas no canvas do preview.
 *
 * Precisa espelhar o que o compilador ASS produz no render — mesma posição, mesmo
 * quebra de linha, mesmo destaque de palavra. Sem isso, o usuário posiciona a legenda no
 * preview e ela sai em outro lugar no arquivo final, que é a queixa mais comum sobre
 * editores web.
 */
import {
  CAPTION_SAFE_WIDTH,
  fadeGain,
  wrapCaptionWords,
  type CaptionWord,
  type Sequence,
  type TimelineItem,
} from '@easyedit/timeline'

export function drawText(
  ctx: CanvasRenderingContext2D,
  item: TimelineItem,
  sequence: Sequence,
  localTime = 0,
): void {
  const text = item.text
  if (!text) return

  const lines = text.content.split('\n')
  const lineHeight = text.fontSize * text.lineHeight

  // Escala e rotação entram como transformação do CONTEXTO, não como corpo de fonte
  // maior: assim o contorno, a sombra e a caixa de fundo escalam junto, e o resultado
  // casa com o `\fscx/\fscy` do ASS, que também escala o glifo inteiro.
  const alpha = item.transform.opacity * fadeGain(item, localTime)
  if (alpha <= 0) return

  ctx.save()
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha))
  ctx.translate(sequence.width / 2 + item.transform.x, sequence.height / 2 + item.transform.y)
  if (item.transform.rotation) ctx.rotate((item.transform.rotation * Math.PI) / 180)
  if (item.transform.scale !== 1) ctx.scale(item.transform.scale, item.transform.scale)

  // Depois da transformação, o desenho acontece em torno da origem.
  const x = 0
  ctx.font = fontString(text.fontWeight, text.italic, text.fontSize, text.fontFamily)
  ctx.textAlign = text.align
  ctx.textBaseline = 'middle'

  const totalHeight = lines.length * lineHeight
  let cursorY = -totalHeight / 2 + lineHeight / 2

  for (const line of lines) {
    if (text.background) {
      const metrics = ctx.measureText(line)
      const padding = text.background.padding
      const boxWidth = metrics.width + padding * 2
      const boxX = text.align === 'center' ? x - boxWidth / 2 : text.align === 'right' ? x - boxWidth : x - padding
      ctx.save()
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha * (text.background.opacity ?? 1)))
      ctx.fillStyle = text.background.color
      roundRect(ctx, boxX, cursorY - lineHeight / 2, boxWidth, lineHeight, text.background.radius)
      ctx.fill()
      ctx.restore()
    }
    if (text.shadow) {
      ctx.shadowColor = text.shadow.color
      ctx.shadowOffsetX = text.shadow.x
      ctx.shadowOffsetY = text.shadow.y
      ctx.shadowBlur = text.shadow.blur
    }
    if (text.stroke && text.stroke.width > 0) {
      ctx.lineWidth = text.stroke.width
      ctx.strokeStyle = text.stroke.color
      ctx.lineJoin = 'round'
      ctx.strokeText(line, x, cursorY)
    }
    ctx.fillStyle = text.color
    ctx.fillText(line, x, cursorY)
    ctx.shadowColor = 'transparent'
    cursorY += lineHeight
  }
  ctx.restore()
}

export function drawCaption(
  ctx: CanvasRenderingContext2D,
  item: TimelineItem,
  time: number,
  sequence: Sequence,
): void {
  const caption = item.caption
  if (!caption || caption.words.length === 0) return

  const visible = wordsAt(caption.words, caption.style, time)
  if (visible.length === 0) return

  const fontSize = caption.fontSize
  const font = fontString(caption.fontWeight, false, fontSize, caption.fontFamily)
  ctx.save()
  ctx.font = font
  ctx.textBaseline = 'middle'

  // A quebra vem da função COMPARTILHADA, não da métrica do canvas: o compilador ASS
  // precisa chegar às mesmas linhas, e ele não tem canvas nem necessariamente a fonte.
  // A largura de cada linha, essa sim, é medida aqui — é só para centralizar.
  const lines = wrapCaptionWords(visible, {
    fontSize,
    maxWidth: sequence.width * CAPTION_SAFE_WIDTH,
    maxWordsPerLine: caption.maxWordsPerLine,
    uppercase: caption.uppercase,
  })
  const lineHeight = fontSize * 1.25
  const baseY = sequence.height * caption.positionY
  let cursorY = baseY - ((lines.length - 1) * lineHeight) / 2

  for (const line of lines) {
    const widths = line.map((w) => ctx.measureText(displayText(w, caption.uppercase)).width)
    const spaceWidth = ctx.measureText(' ').width
    const totalWidth = widths.reduce((a, b) => a + b, 0) + spaceWidth * (line.length - 1)
    let cursorX =
      caption.align === 'left'
        ? sequence.width * 0.07
        : caption.align === 'right'
          ? sequence.width * 0.93 - totalWidth
          : (sequence.width - totalWidth) / 2

    if (caption.background) {
      const padding = caption.background.padding || fontSize * 0.25
      ctx.save()
      ctx.globalAlpha = caption.background.opacity ?? 1
      ctx.fillStyle = caption.background.color
      roundRect(
        ctx,
        cursorX - padding,
        cursorY - lineHeight / 2 - padding / 2,
        totalWidth + padding * 2,
        lineHeight + padding,
        caption.background.radius || 6,
      )
      ctx.fill()
      ctx.restore()
    }

    for (let i = 0; i < line.length; i++) {
      const word = line[i]!
      const label = displayText(word, caption.uppercase)
      // A palavra corrente é a que o karaokê está "cantando" — é o que dá o ritmo.
      const isActive = time >= word.start && time < word.end
      const color = isActive
        ? caption.activeColor
        : word.highlight
          ? caption.highlightColor
          : caption.color

      if (caption.shadow) {
        ctx.shadowColor = caption.shadow.color
        ctx.shadowOffsetX = caption.shadow.x
        ctx.shadowOffsetY = caption.shadow.y
        ctx.shadowBlur = caption.shadow.blur
      }
      if (caption.stroke && caption.stroke.width > 0) {
        ctx.lineWidth = caption.stroke.width
        ctx.strokeStyle = caption.stroke.color
        ctx.lineJoin = 'round'
        ctx.miterLimit = 2
        ctx.strokeText(label, cursorX, cursorY)
      }
      ctx.fillStyle = color
      ctx.fillText(label, cursorX, cursorY)
      ctx.shadowColor = 'transparent'
      cursorX += widths[i]! + spaceWidth
    }
    cursorY += lineHeight
  }
  ctx.restore()
}

function displayText(word: CaptionWord, uppercase: boolean): string {
  return uppercase ? word.text.toUpperCase() : word.text
}

/** Quais palavras estão na tela agora, conforme o estilo. */
function wordsAt(words: CaptionWord[], style: string, time: number): CaptionWord[] {
  if (style === 'word') {
    const current = words.find((w) => time >= w.start && time < w.end)
    return current ? [current] : []
  }
  // Nos demais estilos o bloco inteiro fica visível; o destaque muda com o tempo.
  return words
}

function fontString(weight: number, italic: boolean, size: number, family: string): string {
  return `${italic ? 'italic ' : ''}${weight} ${size}px "${family}", Inter, system-ui, sans-serif`
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + width, y, x + width, y + height, r)
  ctx.arcTo(x + width, y + height, x, y + height, r)
  ctx.arcTo(x, y + height, x, y, r)
  ctx.arcTo(x, y, x + width, y, r)
  ctx.closePath()
}
